import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Button,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Tooltip,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  AttachMoney as AttachMoneyIcon,
  Group as GroupIcon,
  Paid as PaidIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Hotel as HotelIcon,
  CalendarMonth as CalendarMonthIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { getAssignments, getPaymentStatus } from '../../../services/guestAssignmentService';

export default function PaymentStatusTracker() {
  const [assignments, setAssignments] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, paid, unpaid, partial, completed
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  useEffect(() => {
    loadAssignments();
  }, []);

  const loadAssignments = async () => {
    setLoadingList(true);
    try {
      const data = await getAssignments({ limit: 100 });
      setAssignments(data.data || []);
    } catch (err) {
      setError(err.message || 'Error al cargar asignaciones');
    } finally {
      setLoadingList(false);
    }
  };

  const handleSelectAssignment = async (assignment) => {
    setSelectedAssignment(assignment);
    setLoadingPayment(true);
    setError(null);
    try {
      const result = await getPaymentStatus(assignment._id);
      setPaymentData(result.data);
    } catch (err) {
      setError(err.message || 'Error al obtener estado de pagos');
    } finally {
      setLoadingPayment(false);
    }
  };

  const handleBack = () => {
    setSelectedAssignment(null);
    setPaymentData(null);
    setSearchTerm('');
    setFilterStatus('all');
  };

  const handleRefresh = () => {
    if (selectedAssignment) {
      handleSelectAssignment(selectedAssignment);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatMXN = (amount) => {
    if (!amount && amount !== 0) return '—';
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount);
  };

  // Filter guests
  const filteredGuests = (paymentData?.guests || []).filter(guest => {
    const matchesSearch = !searchTerm ||
      guest.guestName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.roomName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (guest.guestWhatsapp && guest.guestWhatsapp.includes(searchTerm)) ||
      (guest.booking?.bookingId && guest.booking.bookingId.toLowerCase().includes(searchTerm.toLowerCase()));

    let matchesFilter = true;
    if (filterStatus === 'paid') matchesFilter = guest.hasPaid;
    else if (filterStatus === 'unpaid') matchesFilter = !guest.hasPaid;
    else if (filterStatus === 'completed') matchesFilter = guest.booking?.paymentStatus === 'completed';
    else if (filterStatus === 'partial') matchesFilter = guest.booking?.paymentStatus === 'partial';

    return matchesSearch && matchesFilter;
  });

  const getPaymentChip = (guest) => {
    if (!guest.hasPaid) {
      return (
        <Chip
          icon={<CancelIcon />}
          label="Sin reserva"
          size="small"
          sx={{
            bgcolor: '#fce4ec',
            color: '#c62828',
            border: '1px solid #ef9a9a',
            fontWeight: 500,
          }}
        />
      );
    }
    if (guest.booking?.paymentStatus === 'completed') {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Pagado completo"
          size="small"
          sx={{
            bgcolor: '#e8f5e9',
            color: '#2e7d32',
            border: '1px solid #a5d6a7',
            fontWeight: 500,
          }}
        />
      );
    }
    if (guest.booking?.paymentStatus === 'partial') {
      return (
        <Chip
          icon={<HourglassEmptyIcon />}
          label="Pago parcial"
          size="small"
          sx={{
            bgcolor: '#fff3e0',
            color: '#e65100',
            border: '1px solid #ffcc80',
            fontWeight: 500,
          }}
        />
      );
    }
    return (
      <Chip
        icon={<PendingIcon />}
        label="Pendiente"
        size="small"
        sx={{
          bgcolor: '#fff9c4',
          color: '#f57f17',
          border: '1px solid #fff176',
          fontWeight: 500,
        }}
      />
    );
  };

  // ───── ASSIGNMENT LIST VIEW ─────
  if (!selectedAssignment) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 3,
            backgroundColor: '#ffffff',
            border: '1px solid #d4af37',
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{ color: '#1a1a1a', fontFamily: 'Georgia, serif' }}
          >
            Control de Pagos
          </Typography>
          <Typography variant="body1" sx={{ color: '#666666', mb: 1 }}>
            Selecciona un evento para ver el estado de pago de cada huésped registrado
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loadingList ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#d4af37' }} />
          </Box>
        ) : assignments.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <Typography color="text.secondary">
              No hay asignaciones creadas. Crea una desde "Asignación de Huéspedes".
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {assignments.map((a) => {
              const totalRooms = (a.casaHotelRooms?.length || 0) + (a.boutiqueRooms?.length || 0);
              const filledRooms = [
                ...(a.casaHotelRooms || []),
                ...(a.boutiqueRooms || []),
              ].filter(r => r.guestName && r.guestName.trim()).length;

              return (
                <Grid item xs={12} md={6} lg={4} key={a._id}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: '#d4af37',
                        boxShadow: '0 4px 20px rgba(212,175,55,0.15)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                    onClick={() => handleSelectAssignment(a)}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{ color: '#1a1a1a', fontFamily: 'Georgia, serif', mb: 1 }}
                      >
                        {a.eventName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        {a.brideName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 2 }}>
                        {formatDate(a.createdAt)}
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip
                          icon={a.status === 'completed' ? <CheckCircleIcon /> : <PendingIcon />}
                          label={a.status === 'completed' ? 'Completado' : 'Pendiente'}
                          color={a.status === 'completed' ? 'success' : 'warning'}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                        <Chip
                          icon={<GroupIcon />}
                          label={`${filledRooms} huéspedes`}
                          size="small"
                          sx={{
                            bgcolor: '#f5f5f5',
                            color: '#1a1a1a',
                            border: '1px solid #e0e0e0',
                          }}
                        />
                      </Stack>

                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {filledRooms} / {totalRooms} habitaciones asignadas
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={totalRooms > 0 ? (filledRooms / totalRooms) * 100 : 0}
                          sx={{
                            mt: 0.5,
                            height: 6,
                            borderRadius: 3,
                            bgcolor: '#e0e0e0',
                            '& .MuiLinearProgress-bar': { bgcolor: '#d4af37' },
                          }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    );
  }

  // ───── PAYMENT STATUS DETAIL VIEW ─────
  const summary = paymentData?.summary || {};
  const progressPercent = summary.totalGuests > 0
    ? Math.round((summary.paidGuests / summary.totalGuests) * 100)
    : 0;

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          mb: 3,
          backgroundColor: '#ffffff',
          border: '1px solid #d4af37',
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Regresar a la lista">
            <IconButton
              onClick={handleBack}
              sx={{ color: '#1a1a1a', border: '1px solid #e0e0e0' }}
              size={isMobile ? 'small' : 'medium'}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant={isMobile ? 'h6' : 'h4'}
              noWrap
              sx={{ color: '#1a1a1a', fontFamily: 'Georgia, serif' }}
            >
              Control de Pagos
            </Typography>
            <Typography
              variant={isMobile ? 'body2' : 'h6'}
              noWrap
              sx={{ color: '#d4af37', fontFamily: 'Georgia, serif' }}
            >
              {paymentData?.assignment?.eventName || selectedAssignment.eventName}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              borderColor: '#1a1a1a',
              color: '#1a1a1a',
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              '&:hover': { borderColor: '#1a1a1a', bgcolor: '#f5f5f5' },
            }}
          >
            {isMobile ? '' : 'Actualizar'}
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Snackbar
          open={!!success}
          autoHideDuration={4000}
          onClose={() => setSuccess(null)}
          message={success}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        />
      )}

      {loadingPayment ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#d4af37' }} />
        </Box>
      ) : paymentData ? (
        <>
          {/* Summary Cards */}
          <Grid container spacing={isMobile ? 1.5 : 3} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ textAlign: 'center', py: { xs: 1.5, md: 3 }, px: { xs: 1, md: 2 } }}>
                  <GroupIcon sx={{ fontSize: { xs: 24, md: 36 }, color: '#1a1a1a', mb: 0.5 }} />
                  <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" sx={{ color: '#1a1a1a' }}>
                    {summary.totalGuests || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Huéspedes
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid #2e7d32' }}>
                <CardContent sx={{ textAlign: 'center', py: { xs: 1.5, md: 3 }, px: { xs: 1, md: 2 } }}>
                  <PaidIcon sx={{ fontSize: { xs: 24, md: 36 }, color: '#2e7d32', mb: 0.5 }} />
                  <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" sx={{ color: '#2e7d32' }}>
                    {summary.paidGuests || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Con Reserva
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid #c62828' }}>
                <CardContent sx={{ textAlign: 'center', py: { xs: 1.5, md: 3 }, px: { xs: 1, md: 2 } }}>
                  <CancelIcon sx={{ fontSize: { xs: 24, md: 36 }, color: '#c62828', mb: 0.5 }} />
                  <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" sx={{ color: '#c62828' }}>
                    {summary.unpaidGuests || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Sin Reserva
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={3}>
              <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid #d4af37' }}>
                <CardContent sx={{ textAlign: 'center', py: { xs: 1.5, md: 3 }, px: { xs: 1, md: 2 } }}>
                  <AttachMoneyIcon sx={{ fontSize: { xs: 24, md: 36 }, color: '#d4af37', mb: 0.5 }} />
                  <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" sx={{ color: '#d4af37' }}>
                    {progressPercent}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avance de Pagos
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progressPercent}
                    sx={{
                      mt: 1,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: progressPercent === 100 ? '#2e7d32' : '#d4af37',
                      },
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Buscar por nombre, habitación, teléfono o ID de reserva..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#e0e0e0' },
                      '&:hover fieldset': { borderColor: '#1a1a1a' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filtrar por estado</InputLabel>
                  <Select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    label="Filtrar por estado"
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="paid">Con reserva</MenuItem>
                    <MenuItem value="unpaid">Sin reserva</MenuItem>
                    <MenuItem value="completed">Pago completo</MenuItem>
                    <MenuItem value="partial">Pago parcial</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} sx={{ textAlign: 'right' }}>
                <Typography variant="body2" color="text.secondary">
                  Mostrando {filteredGuests.length} de {paymentData?.guests?.length || 0}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Guest List — Cards en mobile, Table en desktop */}
          {isMobile ? (
            /* ───── MOBILE: Card Layout ───── */
            <Stack spacing={1.5}>
              {filteredGuests.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                  <Typography color="text.secondary">No se encontraron resultados</Typography>
                </Paper>
              ) : (
                filteredGuests.map((guest, idx) => (
                  <Card
                    key={`${guest.roomId}-${idx}`}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      bgcolor: guest.hasPaid ? '#fff' : '#fffde7',
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      {/* Row 1: Name + Status */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#1a1a1a' }}>
                          {guest.guestName}
                        </Typography>
                        {getPaymentChip(guest)}
                      </Stack>

                      {/* Row 2: Hotel + Room */}
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Chip
                          icon={<HotelIcon />}
                          label={guest.hotel}
                          size="small"
                          sx={{
                            bgcolor: guest.hotel === 'Boutique' ? '#e8eaf6' : '#fff3e0',
                            color: guest.hotel === 'Boutique' ? '#283593' : '#e65100',
                            border: `1px solid ${guest.hotel === 'Boutique' ? '#9fa8da' : '#ffcc80'}`,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                            height: 24,
                          }}
                        />
                        <Typography variant="body2" fontWeight="600" sx={{ color: '#333' }}>
                          {guest.roomName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {guest.bed} · Cap: {guest.capacity}
                        </Typography>
                      </Stack>

                      {/* WhatsApp if exists */}
                      {guest.guestWhatsapp && (
                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                          📱 {guest.guestWhatsapp}
                        </Typography>
                      )}

                      {/* Booking details if exists */}
                      {guest.booking && (
                        <>
                          <Divider sx={{ my: 1 }} />
                          <Stack spacing={0.5}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="caption" fontWeight="bold" sx={{ color: '#d4af37' }}>
                                {guest.booking.bookingId}
                              </Typography>
                              <Typography variant="caption" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                                {formatMXN(guest.booking.totalPrice)}
                              </Typography>
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              {guest.booking.roomName} · {formatDate(guest.booking.checkIn)} — {formatDate(guest.booking.checkOut)} ({guest.booking.nights}n)
                            </Typography>
                            {guest.booking.discountCode && (
                              <Chip
                                label={`Código: ${guest.booking.discountCode}`}
                                size="small"
                                sx={{
                                  bgcolor: '#f3e5f5',
                                  color: '#7b1fa2',
                                  border: '1px solid #ce93d8',
                                  fontSize: '0.65rem',
                                  height: 20,
                                  alignSelf: 'flex-start',
                                }}
                              />
                            )}
                          </Stack>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </Stack>
          ) : (
            /* ───── DESKTOP: Table Layout ───── */
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}
            >
              <Table>
                <TableHead sx={{ bgcolor: '#fafafa' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a', borderBottom: '2px solid #d4af37' }}>
                      HOTEL
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a', borderBottom: '2px solid #d4af37' }}>
                      HABITACIÓN
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a', borderBottom: '2px solid #d4af37' }}>
                      HUÉSPED
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a', borderBottom: '2px solid #d4af37' }}>
                      WHATSAPP
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a', borderBottom: '2px solid #d4af37' }}>
                      ESTADO DE PAGO
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a', borderBottom: '2px solid #d4af37' }}>
                      RESERVA
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1a1a1a', borderBottom: '2px solid #d4af37' }}>
                      DETALLES
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredGuests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No se encontraron resultados
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredGuests.map((guest, idx) => (
                      <TableRow
                        key={`${guest.roomId}-${idx}`}
                        hover
                        sx={{
                          '&:hover': { bgcolor: '#fafafa' },
                          bgcolor: guest.hasPaid ? 'transparent' : '#fffde7',
                        }}
                      >
                        <TableCell>
                          <Chip
                            icon={<HotelIcon />}
                            label={guest.hotel}
                            size="small"
                            sx={{
                              bgcolor: guest.hotel === 'Boutique' ? '#e8eaf6' : '#fff3e0',
                              color: guest.hotel === 'Boutique' ? '#283593' : '#e65100',
                              border: `1px solid ${guest.hotel === 'Boutique' ? '#9fa8da' : '#ffcc80'}`,
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: '#1a1a1a' }}>
                            {guest.roomName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {guest.bed} · Cap: {guest.capacity}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600" sx={{ color: '#1a1a1a' }}>
                            {guest.guestName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {guest.guestWhatsapp ? (
                            <Typography variant="body2" sx={{ color: '#666' }}>
                              {guest.guestWhatsapp}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {getPaymentChip(guest)}
                        </TableCell>
                        <TableCell>
                          {guest.booking ? (
                            <Box>
                              <Typography variant="body2" fontWeight="bold" sx={{ color: '#d4af37' }}>
                                {guest.booking.bookingId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {guest.booking.roomName}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {guest.booking ? (
                            <Box>
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                <AttachMoneyIcon sx={{ fontSize: 14, color: '#2e7d32' }} />
                                <Typography variant="caption">
                                  Total: {formatMXN(guest.booking.totalPrice)}
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                <CalendarMonthIcon sx={{ fontSize: 14, color: '#666' }} />
                                <Typography variant="caption">
                                  {formatDate(guest.booking.checkIn)} — {formatDate(guest.booking.checkOut)} ({guest.booking.nights}n)
                                </Typography>
                              </Stack>
                              {guest.booking.discountCode && (
                                <Chip
                                  label={`Código: ${guest.booking.discountCode}`}
                                  size="small"
                                  sx={{
                                    bgcolor: '#f3e5f5',
                                    color: '#7b1fa2',
                                    border: '1px solid #ce93d8',
                                    fontSize: '0.65rem',
                                    height: 20,
                                  }}
                                />
                              )}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No se encontró reserva
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : null}
    </Container>
  );
}
